import React, { useEffect, useContext, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { AppContext, DispatchContext } from "../AppContext";
import Service from "../services/service";
import * as actions from "../actions/actions";
import { toggleCaptcha, config, resolveNotificationMessage } from '../helpers/constants';
import { Link, Divider, Stepper, Step, StepLabel, Box, Paper, Typography, Button } from "@material-ui/core";
import FullScreenPage from "./FullScreenPage";
import IconLabel from "./IconLabel";
import { ReactComponent as CheckedIcon } from '../assets/icons/checked.svg';
import LoadingScreen from "./LoadingScreen";
import WizardUploadPanel from "./WizardUploadPanel";
import WizardWelcomePanel from "./WizardWelcomePanel";
import WizardSelectProductPanel from "./WizardSelectProductPanel";
import CheckoutLoginPanel from "./CheckoutLoginPanel";
import { initialState } from "../reducer/InitialState";
import InvoiceAddressForm from './InvoiceAddressForm';
import PriceCard from './PriceCard';
import CheckoutSelectPaymentPanel from "./CheckoutSelectPaymentPanel";
import FileCard from "./FileCard";


const useStyles = makeStyles((theme) => ({
  paper: {
    width: 880,
    minHeight: 640,
    position: "relative",
    boxShadow: "0px 0px 5px 5px rgba(0, 0, 0, 0.25)",
    "&:hover": {
      boxShadow: "0px 0px 7px 7px rgba(0, 0, 0, 0.4)",
      transition: "box-shadow 0.3s ease-in-out",
    }
  }
}));

export default function WizardPanel(props) {
  const dispatch = useContext(DispatchContext);
  const state = useContext(AppContext);
  const s = state.strings;
  const wz = state.checkout.wizard;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [newLogin, setNewLogin] = useState(false);  // indiciates whether a user has just logged in in this wizard session to prevent to remove the completed step

  useEffect(() => {
    return () => {
      reset();
    }
  }, []);

  useEffect(() => {
    toggleCaptcha(state.user.loggedIn);
  }, [state.user.loggedIn]);
  
  // calculate available / required credits and required product when the status of the uploaded file or selected product changes
  useEffect(() => {
    if (state.uploadedFilesStatus[0]) {
      const uploadId = state.uploadedFilesStatus[0].UploadId;
      const requiredCredits = Math.ceil(state.uploadedFilesStatus[0].Words / 250.0);
      const freeCredits = (state.user.loggedIn && !state.user.guest) ? 0 : 10;
      const selectedProduct = state.checkout.products.filter(p => p.id == state.checkout.productId)[0];
      const availableCreditsAfterCheckout = state.user.totalCredits + (selectedProduct ?
        selectedProduct.id == 30110 ? freeCredits :
          selectedProduct.credits + freeCredits : 0);
      const missingCredits = requiredCredits - availableCreditsAfterCheckout;
      const requiredProductId = missingCredits <= freeCredits ? 30110 :
        missingCredits <= (100 + freeCredits) ? 30111 :
          missingCredits <= (250 + freeCredits) ? 30112 :
            missingCredits <= (500 + freeCredits) ? 30113 : 30114;
      dispatch(actions.setCheckoutDetails({ wizard: { ...wz, uploadId: uploadId, freeCredits: freeCredits, requiredCredits: requiredCredits, availableCreditsAfterCheckout: availableCreditsAfterCheckout, requiredProductId: requiredProductId } }));
    }
  }, [state.uploadedFilesStatus, state.user, state.checkout.productId]);

  // fetch info of uploaded file if uploadid or upload state changed 
  useEffect(() => {
    Service.getUploadedFileInfo(state.checkout.wizard.uploadId).then((res) => {
      dispatch(actions.setCheckoutDetails({ file: { ...res.data } }));
    });
  }, [state.uploadedFilesStatus, state.checkout.wizard.uploadId]);

  const updateInvoiceAddress = () => {
    setLoading(true);
    // Update invoice address (only filled out fields to make sure possibly expired license code does not cause an error message in backend)
    Service.updateUser({ name: state.user.name, companyName: state.user.companyName, address1: state.user.address1, address2: state.user.address2, zip: state.user.zip, city: state.user.city, country: state.user.country, vatId: state.user.vatId }).then(res => {
      setLoading(false);
      if (res.success) {
        dispatch(actions.setCheckoutDetails({ wizard: { ...wz, step: wz.step + 1 } }));
        Service.getUserInfo().then(getInfoRes => {
          dispatch(actions.setUserDetails(getInfoRes.data));
        });
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
      }
    });
  }

  const createOrder = () => {
    Service.createOrder(state.checkout.products.find(p => { return p.id === state.checkout.productId }), state.checkout.currency, state.checkout.paymentId, state.checkout.taxIncluded, state.checkout.taxMode, state.checkout.vatRate, wz.uploadId).then(res => {
      processPayment(res);
    });
  }

  const processPayment = (res) => {
    if (res.success) {
      if (res.data.payment && res.data.payment._links && res.data.payment._links.checkout) {
        let url = res.data.payment._links.checkout.href;
        dispatch(actions.navigate_to(url));
      } else {
        // order succeeded, but no link to checkout provided (either error or mandate payment) => check invoice status and bring up pay invoice dialog
        let url = config.frontendUrl + "checkout/" + res.data.invoiceId;
        dispatch(actions.navigate_to(url));
      }
    } else {
      dispatch(actions.showFullscreenLoading(false));
      dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
    }
  }

  const closeWizard = () => {
    reset();
    if (state.user.loggedIn) {
      dispatch(actions.navigate_to("/dashboard"));
    } else {
      window.location.href = config.backendUrl + s.links.home;
    }
  }


  const steps = [
    {
      key: 'upload',
      title: s.wizard.uploadFile,
      completed: wz.requiredCredits > 0,
      backLabel: s.common.close,
      backAction: closeWizard,
      visible: true
    },
    {
      key: 'package',
      title: s.wizard.selectPackage,
      completed: state.checkout.productId,
      // visible if no upload has been done OR more credits are required 
      visible: !wz.requiredCredits || state.checkout.invoiceId || ((wz.requiredCredits > 0) && (wz.requiredCredits > state.user.totalCredits))
    },
    {
      key: 'login',
      title: s.wizard.loginOrRegister,
      completed: state.user.loggedIn && !state.user.guest,
      // visible if user is logged in as non-guest or has just logged in in this wizard session
      visible: newLogin || !state.user.loggedIn || state.user.guest
    },
    {
      key: 'billing',
      title: s.wizard.billingAddress,
      completed: state.user.invoiceAddressComplete,
      nextAction: updateInvoiceAddress,
      // visible if selected real product
      visible: (state.checkout.productId && state.checkout.productId != 30110) || (state.checkout.invoiceId)
    },
    {
      key: 'payment',
      title: s.wizard.payment,
      completed: state.checkout.paymentId,
      nextLabel: s.paymentDialog.purchase,
      nextAction: createOrder,
      // visible if selected real product
      visible: (state.checkout.productId && state.checkout.productId != 30110) || (state.checkout.invoiceId)
    },
    {
      key: 'check',
      title: s.wizard.plagCheck,
      completed: false,
      visible: true,
      nextHidden: true,
      backLabel: state.checkout.invoiceId && s.common.cancel,
      backAction: state.checkout.invoiceId && closeWizard
    }
  ];

  const maxStep = steps.filter(s => s.visible).length - 1;
  const activeStep = steps.filter(s => s.visible)[Math.max(0, Math.min(maxStep, wz.step)) || 0];
  const selectedProduct = state.checkout.productId ? state.checkout.products.filter(p => p.id == state.checkout.productId)[0] : null;

  const onNextButtonClicked = () => {
    if (activeStep && activeStep.nextAction) {
      activeStep.nextAction();
    } else {
      dispatch(actions.setCheckoutDetails({ wizard: { ...wz, step: wz.step + 1 } }));
    }
  };



  const onLoginNowClicked = () => {
    dispatch(actions.navigate_to("/login"));
  };

  const onBackButtonClicked = () => {
    if (activeStep && activeStep.backAction) {
      activeStep.backAction();
    } else {
      dispatch(actions.setCheckoutDetails({ wizard: { ...wz, step: wz.step - 1 } }));
    }
  };


  const reset = () => {
    dispatch(actions.cancelFilesUpload());
    dispatch(actions.setUploadedFilesStatus({ Files: [] }))
    dispatch(actions.setCheckoutDetails({ file: {}, wizard: initialState.checkout.wizard, productId: null }));
  }

  const nextButtonDisabled = () => {
    let enabled = activeStep && activeStep.completed;
    return !enabled;
  }

  const handleCheckoutChange = (newValue, key) => {
    dispatch(actions.setCheckoutDetails({ [key]: newValue }));
  }

  const onLoggedIn = () => {
    // reset steps handled by reducer
    dispatch(actions.setCheckoutDetails({ productId: null }));
  }

  const onRegistered = (mail) => {
    dispatch(actions.openToast({ message: s.notificationMessage.loggedIn.format(mail) }));
    setNewLogin(true);
    onNextButtonClicked();
  }

  const onSetUserDetails = (user) => {
    dispatch(actions.setUserDetails(user));
  }

  const onStartPlagCheckButonClicked = () => {
    let apiData = {
      web: true,
      lib: state.user.settings.searchInLibrary,
      usewhitelist: state.user.settings.useAuthWhitelist,
      extractlinks: state.user.settings.autoAcceptSources,
      addtolibrary: state.user.settings.addToLibrary,
      uploadids: [wz.uploadId],
      truncate: state.user.totalCredits < wz.requiredCredits,
      hold: false
    };
    Service.NewPlagCheck(apiData).then(res => {
      if (res.success) {
        dispatch(actions.setDraftReport({}));
        dispatch(actions.navigate_to('/plagiarism/reports/' + res.data[0].ReportId));
        if (res.message) {
          dispatch(actions.openToast({ type: "warning", message: resolveNotificationMessage(res.message, s.notificationMessage) }));
        } else {
          dispatch(actions.openToast({ message: s.notificationMessage.plagScanStarted }));
        }
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
      }
    })
  }


  return (
    <FullScreenPage>
      <Paper elevation={5} className={classes.paper}>
        <LoadingScreen status={loading} />
        <div style={{ padding: 24 }}>
          <Typography variant="h6">{s.wizard.title}</Typography>
          <Divider style={{ marginTop: 8 }} />
          <Stepper activeStep={wz.step} alternativeLabel>
            {steps.filter(s => s.visible).map((s, i) =>
              <Step key={s.id} >
                <StepLabel>{s.title}</StepLabel>
              </Step>
            )}
          </Stepper>
          {activeStep &&
            <div style={{ display: "flex", flexDirection: "column", height: 370 }}>
              {activeStep.key == 'upload' &&
                (state.user.loggedIn ?
                  <WizardUploadPanel /> :
                  <WizardWelcomePanel />)
              }
              {activeStep.key == 'package' && <WizardSelectProductPanel />}
              {activeStep.key == 'login' && (
                !state.user.loggedIn || state.user.guest ? <CheckoutLoginPanel wizard onLoggedIn={onLoggedIn} onRegistered={onRegistered} /> :
                  <IconLabel centered mt={24} label={s.payment.loggedInAs.format(state.user.mail)} />)
              }
              {activeStep.key == 'billing' &&
                <div style={{ display: "flex", width: "80%", alignSelf: "center" }}>
                  {selectedProduct &&
                    <div style={{ width: "35%", alignSelf: "center" }}>
                      <PriceCard title={selectedProduct.nameShort} text1={s.paymentDialog.priceCardCredits.format(selectedProduct.credits)} text2={s.paymentDialog.priceCardPages.format(selectedProduct.credits)}
                        text3={selectedProduct.users}
                        price={selectedProduct.cost[state.checkout.currency]}
                        currency={state.checkout.currency}
                        selected={true}
                        taxIncluded={state.checkout.taxIncluded} 
                        vatRate={state.checkout.vatRate} />
                    </div>}
                  <div style={{ width: "65%" }}>
                    <InvoiceAddressForm user={state.user} setUser={onSetUserDetails} />
                  </div>
                </div>}
              {activeStep.key == 'payment' &&
                <CheckoutSelectPaymentPanel wizard />
              }
              {activeStep.key == 'check' &&
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", alignSelf: "center", width: "80%" }}>
                  <IconLabel mt={16} mb={36} type="check">{s.wizard.readyToStart}</IconLabel>
                  <FileCard id={state.checkout.file.uploadId} title={state.checkout.file.name} text1={s.newCheck.uploadFileWords.format(state.checkout.file.words, state.checkout.file.pages)} text2={s.common.scanCreditsCount.format(Math.min(state.checkout.file.pages, state.user.totalCredits))} />
                  {state.user.totalCredits < wz.requiredCredits && <Typography style={{ marginTop: 8 }} variant="body2" color="error">{s.wizard.textTruncated.format(state.user.totalCredits)}</Typography>}
                  <Box mt={8} />
                  <Button style={{ width: 300, height: 48 }} variant="contained" color="primary" onClick={onStartPlagCheckButonClicked}>{s.startPlagiarism}</Button>
                </div>
              }

            </div>}
          <Divider />

          <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginTop: 24 }}>
            {wz.step <= 1 && (state.user.guest || !state.user.loggedIn) && <IconLabel label={s.wizard.notLoggedIn} link={<Link href="#" onClick={onLoginNowClicked}>{s.wizard.loginNow}</Link>} />}
            <div style={{ flex: "1 1 auto" }}></div>
            <Button onClick={onBackButtonClicked} variant="outlined" style={{ minWidth: 150, paddingLeft: 16, paddingRight: 16 }}>{activeStep && activeStep.backLabel ? activeStep.backLabel : s.common.back}</Button>
            {!activeStep.nextHidden &&
              <Button disabled={nextButtonDisabled()} onClick={onNextButtonClicked} variant="contained" style={{ minWidth: 150, paddingLeft: 16, paddingRight: 16, marginLeft: 8 }} color="primary">{activeStep && activeStep.nextLabel ? activeStep.nextLabel : s.common.next}</Button>}
          </div>
        </div>
      </Paper>
    </FullScreenPage>
  );
}
